import React, { FC } from 'react';
import styled from '@emotion/styled';
import { Box, Divider } from '~Styles';
import { useCurrency, useI18n } from '~Hooks';
import Heading from '~ComponentsImproved/Typography/Heading';
import Paragraph from '~ComponentsImproved/Typography/Paragraph';
import { useCashUniverse } from '~Components/CashUniverse';
import AddRemoveIcon from '../AddRemoveIcon';
import { calculateTotalAdded, calculateTotalRemoved } from '~Scenes/CashSources/utils';

interface Props {
  cashFloat: number;
}

const CashRegisterBreakdown: FC<Props> = ({ cashFloat }) => {
  const { i18n } = useI18n();
  const currency = useCurrency();
  const cashUniverse = useCashUniverse();

  return (
    <Box display="flex" flexDirection="column" gap="small">
      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap="xs" alignItems="center">
          <AddRemoveIcon icon="add" size="small" amount={cashUniverse.initialCashRegister || 0} />
          <Paragraph level={4} color="var(--colorBlueGray200)">
            {i18n('CashManagement.InitialCash')}
          </Paragraph>
        </Box>

        <Heading color="var(--colorBlueGray200)" level={4}>
          {currency.format(cashUniverse.initialCashRegister || 0)}
        </Heading>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap="xs" alignItems="center">
          <AddRemoveIcon
            icon="add"
            size="small"
            amount={calculateTotalAdded(cashUniverse.sources)}
          />
          <Paragraph level={4} color="var(--colorBlueGray200)">
            {i18n('CashManagement.Deposited')}
          </Paragraph>
        </Box>

        <Heading color="var(--colorBlueGray200)" level={4}>
          {currency.format(calculateTotalAdded(cashUniverse.sources))}
        </Heading>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap="xs" alignItems="center">
          <AddRemoveIcon
            icon="remove"
            size="small"
            amount={-calculateTotalRemoved(cashUniverse.sources)}
          />
          <Paragraph level={4} color="var(--colorBlueGray200)">
            {i18n('CashManagement.Withdrawn')}
          </Paragraph>
        </Box>

        <Heading color="var(--colorBlueGray200)" level={4}>
          {currency.format(calculateTotalRemoved(cashUniverse.sources))}
        </Heading>
      </Box>

      <Box display="flex" justifyContent="space-between" alignItems="center">
        <Box display="flex" gap="xs" alignItems="center">
          <AddRemoveIcon icon="remove" size="small" amount={cashFloat} />
          <Paragraph level={4} color="var(--colorBlueGray200)">
            {i18n('CashManagement.Float')}
          </Paragraph>
        </Box>

        <Heading color="var(--colorBlueGray200)" level={4}>
          {currency.format(cashFloat)}
        </Heading>
      </Box>

      <Divider />

      <Box display="flex" justifyContent="space-between" alignItems="center" gap="medium">
        <Heading level={4} color="var(--colorWhite)">
          {i18n('CashManagement.AvailableForPayout')}
        </Heading>

        <Heading level={3} color="var(--colorStatusGreen)">
          {cashUniverse &&
            currency.format(
              (cashUniverse.initialCashRegister ?? 0) +
                calculateTotalAdded(cashUniverse.sources) -
                calculateTotalRemoved(cashUniverse.sources) -
                cashFloat,
            )}
        </Heading>
      </Box>
    </Box>
  );
};

export default CashRegisterBreakdown;
